import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
// Critical path: rendered immediately on the calculator route, kept eager.
import AppHeader from './components/shell/AppHeader';
import SubBar from './components/shell/SubBar';
import MobileSplash from './components/shell/MobileSplash';
import ScrollToTop from './components/shell/ScrollToTop';
import { useTooNarrowForCalculator } from './hooks/useTooNarrowForCalculator';
import MeasureTable from './components/shell/MeasureTable';
import KetteUndSkizze from './components/shell/sketch/KetteUndSkizze';
import { useSketchPaste } from './components/shell/sketch/useSketch';
import ResultsPanel from './components/shell/ResultsPanel';
import CommandPalette from './components/shell/CommandPalette';
import AuthGuard from './components/auth/AuthGuard';
import TourBanner from './tour/TourBanner';
import { useTourStore } from './tour/tourStore';
import TourStarter from './tour/TourStarter';
import PlausibleAnalytics from './components/PlausibleAnalytics';
import Toasts from './components/Toasts';
import VersionsPanel from './components/versions/VersionsPanel';
import NameVersionDialog from './components/versions/NameVersionDialog';
import VersionPreviewBanner from './components/versions/VersionPreviewBanner';
import PrivacyNotice from './components/PrivacyNotice';
import { useAutoSave } from './hooks/useAutoSave';
import { useRecoveryAutoSave } from './hooks/useRecoveryAutoSave';
import RecoveryBanner from './components/RecoveryBanner';
import { clearRecovery } from './lib/recovery';
import { useToleranceStore } from './stores/toleranceStore';
import { useAuthStore, selectIsFree } from './stores/authStore';
// useTheme + useDensity also carry module-load side effects (apply
// body classes before React mounts to prevent FOUC). AppHeader's
// ThemeSwitch consumes useTheme directly; useDensity stays dormant
// until Phase 5's Settings consolidation surfaces a UI toggle.
import { useTheme } from './hooks/useTheme';
import { useDensity } from './hooks/useDensity';

// Off-path screens: code-split so they only ship when the user navigates there.
const LoginPage          = lazy(() => import('./components/auth/LoginPage'));
const RegisterPage       = lazy(() => import('./components/auth/RegisterPage'));
const ResetPasswordPage  = lazy(() => import('./components/auth/ResetPasswordPage'));
const ProjectsPage       = lazy(() => import('./components/ProjectsPage'));
const SettingsLayout     = lazy(() => import('./components/settings/SettingsLayout'));
const AccountSettings    = lazy(() => import('./components/settings/AccountSettings'));
const ProfileSettings    = lazy(() => import('./components/settings/ProfileSettings'));
const BillingSettings    = lazy(() => import('./components/settings/BillingSettings'));
const MonteCarloSettings = lazy(() => import('./components/settings/MonteCarloSettings'));
const DisplaySettings    = lazy(() => import('./components/settings/DisplaySettings'));
const AdminLayout        = lazy(() => import('./components/admin/AdminLayout'));
const AdminDashboard     = lazy(() => import('./components/admin/AdminDashboard'));
const UserList           = lazy(() => import('./components/admin/UserList'));
const AuditLog           = lazy(() => import('./components/admin/AuditLog'));
const TermsPage              = lazy(() => import('./components/legal/TermsPage'));
const ConsumerTermsPage      = lazy(() => import('./components/legal/ConsumerTermsPage'));
const BusinessTermsPage      = lazy(() => import('./components/legal/BusinessTermsPage'));
const PrivacyPage            = lazy(() => import('./components/legal/PrivacyPage'));
const SubprocessorsPage      = lazy(() => import('./components/legal/SubprocessorsPage'));
const AvvPage                = lazy(() => import('./components/legal/AvvPage'));
const ImprintPage            = lazy(() => import('./components/legal/ImprintPage'));
const SlaPage                = lazy(() => import('./components/legal/SlaPage'));
const ServiceDescriptionPage = lazy(() => import('./components/legal/ServiceDescriptionPage'));
const FAQPage                = lazy(() => import('./components/legal/FAQPage'));
const NotFoundPage           = lazy(() => import('./components/NotFoundPage'));

// Skeleton fallback for lazy-loaded routes. Previously a full-bleed
// spinner that read as "the app crashed" — now mimics the actual
// content shape (header bar + card column) so the visual transition
// from skeleton to real content is smooth even on slow connections.
//
// Tailwind's `animate-pulse` does all the work.
function RouteFallback() {
  return (
    <div className="min-h-screen bg-slate-100 animate-pulse" aria-hidden="true">
      {/* Header bar mimic */}
      <div className="bg-slate-800 h-12 md:h-14 flex items-center px-4 gap-3">
        <div className="h-6 w-32 bg-slate-700 rounded" />
        <div className="flex-1" />
        <div className="hidden sm:block h-7 w-16 bg-slate-700 rounded" />
        <div className="hidden sm:block h-7 w-16 bg-slate-700 rounded" />
        <div className="h-7 w-7 bg-slate-700 rounded-full" />
      </div>
      {/* Centered content placeholder card */}
      <div className="max-w-2xl mx-auto p-4 md:p-8 space-y-4">
        <div className="h-8 w-2/3 bg-slate-300 rounded-xl" />
        <div className="h-4 w-1/2 bg-slate-200 rounded" />
        <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4 mt-6">
          <div className="h-4 w-1/3 bg-slate-200 rounded" />
          <div className="h-10 w-full bg-slate-100 rounded-xl" />
          <div className="h-4 w-1/4 bg-slate-200 rounded" />
          <div className="h-10 w-full bg-slate-100 rounded-xl" />
          <div className="h-10 w-32 bg-slate-200 rounded-xl mt-2" />
        </div>
      </div>
      <span className="sr-only">Lädt …</span>
    </div>
  );
}

function CalculatorApp() {
  // Mobile gate per the mobile-policy memo — calculator is desktop-
  // only (the multi-column table model has nowhere to go on a phone).
  // Splash short-circuits the whole calc shell; auth / settings /
  // legal stay responsive on their own routes.
  const tooNarrow = useTooNarrowForCalculator();
  // Im Splash ist kein TourStarter gemountet: eine angeforderte Tour würde
  // sonst liegen bleiben und später überraschend starten.
  useEffect(() => {
    if (tooNarrow) useTourStore.getState().startErledigt();
  }, [tooNarrow]);

  // Debounced auto-save for Pro users with a project + calculation
  // selected. No-op for Free / standalone editing.
  useAutoSave();
  // Local Recovery-Snapshot in localStorage (Task #35) — Free + Pro,
  // 1 s Debounce + beforeunload-Sofortspeicherung. Ersetzt den
  // früheren useUnsavedChangesGuard, der mit einem Browser-Dialog
  // beim Reload nervte und keinen tatsächlichen State retten konnte.
  useRecoveryAutoSave();

  // Pro auto-counted gate. The store's calculate() no-ops while
  // `counted === false`; Free users flip it via an explicit BERECHNEN
  // click (Phase 5 surfaces that button), Pro users have unlimited
  // calcs and shouldn't need it — as soon as there's at least one
  // element, results should appear. Previously this effect lived in
  // the now-deleted Toolbox; this is its new home.
  const isFree = useAuthStore(selectIsFree);
  const elementCount = useToleranceStore((s) => s.elements.length);
  const counted = useToleranceStore((s) => s.counted);
  const startCalculationPro = useToleranceStore((s) => s.startCalculationPro);
  useEffect(() => {
    if (!isFree && elementCount > 0 && !counted) {
      startCalculationPro();
    }
  }, [isFree, elementCount, counted, startCalculationPro]);

  // User-change handler: when the signed-in user actually changes
  // (sign-out, or sign-in as a different account) we wipe the in-
  // memory calc state. Previously the Pro auto-start would flip
  // `counted: true`, and if the user then signed out and signed back
  // in as Free the BERECHNEN button stayed hidden because counted was
  // still true — only F5 fixed it. The ref skips the initial null →
  // real-id transition (that's the auth bootstrap, not a user change).
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const resetSession = useToleranceStore((s) => s.resetSession);
  const lastUserIdRef = useRef(userId);
  useEffect(() => {
    const prev = lastUserIdRef.current;
    if (prev !== null && prev !== userId) {
      resetSession();
      // Plus den Recovery-Snapshot löschen: ein nachfolgender Login
      // (gleicher Browser, anderer Account) soll nicht das Banner mit
      // der Vorbenutzer-Berechnung anbieten — die liegt im selben
      // localStorage, aber gehört semantisch zum vorherigen Login.
      clearRecovery();
    }
    lastUserIdRef.current = userId;
  }, [userId, resetSession]);

  // Masskette show/hide — local, localStorage-backed. Mirrors the
  // handoff prototype's `tweaks.showChain` (app.jsx:1071-1095) so the
  // user can collapse the chain visualization when they want more room
  // for the table; a dashed pill above the table brings it back.
  const [showMasskette, setShowMasskette] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage?.getItem('ui:showMasskette') !== '0';
  });
  const updateShowMasskette = (next) => {
    setShowMasskette(next);
    try { window.localStorage?.setItem('ui:showMasskette', next ? '1' : '0'); } catch { /* ignore */ }
  };

  const tourAktiv = useTourStore((s) => s.aktiv);
  // Strg+V mit reinem Bild fügt die Skizze ein (C7).
  useSketchPaste({ enabled: !tooNarrow });
  if (tooNarrow) return <MobileSplash />;

  return (
    <div className="h-screen flex flex-col bg-page font-sans selection:bg-brand-soft">
      {/* Skip-to-content link. Visually hidden until it receives
          keyboard focus (Tab from the address bar) — then it sits
          fixed top-left so keyboard users can jump past AppHeader +
          SubBar straight into the calculator. */}
      <a
        href="#calc-main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-brand focus:text-white focus:font-bold"
        style={{ borderRadius: 'var(--radius-md)' }}
      >
        Zum Hauptinhalt springen
      </a>
      <AppHeader />
      <SubBar />
      <VersionPreviewBanner />
      {/* Recovery-Banner direkt unter dem SubBar — der eigene Render
          entscheidet selbst, ob ein Snapshot angeboten wird (leere
          Berechnung + kein aktives Cloud-Projekt). */}
      <RecoveryBanner />
      <TourBanner />
      <TourStarter />
      <VersionsPanel />
      <NameVersionDialog />

      {/* Two-column split per the design handoff: left column carries
          the chain visualization + the inline-edit MeasureTable; the
          right column hosts the ResultsPanel as a fixed-width aside.
          Phase 4 swaps ChainDisplay for the new MassketteDiagram and
          reboots ResultsPanel; the layout slot stays the same. */}
      <main
        id="calc-main"
        className="flex-1 overflow-hidden bg-page"
        style={{ display: 'flex', padding: 16, gap: 16 }}
      >
        <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto' }}>
          {/* Ein- und Ausblenden sitzen rechts an derselben Stelle (B3). */}
          {(showMasskette || tourAktiv) ? (
            <KetteUndSkizze onHide={() => updateShowMasskette(false)} />
          ) : (
            <button
              type="button"
              onClick={() => updateShowMasskette(true)}
              className="self-end inline-flex items-center gap-1.5 bg-surface text-ink-3 hover:text-ink-2 border-dashed border-line-strong"
              style={{
                height: 28, padding: '0 12px',
                border: '1px dashed var(--color-line-strong)',
                borderRadius: 8, cursor: 'pointer',
                fontSize: 11.5, fontWeight: 600,
              }}
            >
              <ChevronDown size={12} aria-hidden="true" />
              Maßkette einblenden
            </button>
          )}
          <MeasureTable />
        </div>
        <aside
          className="shrink-0"
          style={{ width: 440, minHeight: 0, display: 'flex', flexDirection: 'column' }}
          aria-label="Ergebnis"
          data-tour="results"
        >
          <ResultsPanel />
        </aside>
      </main>
    </div>
  );
}

function App() {
  // Mount both preference hooks at the root so their body-class +
  // localStorage sync runs on every render of any route. AppHeader's
  // ThemeSwitch is the live consumer of useTheme; useDensity stays
  // dormant until Phase 5 surfaces a density control in Settings.
  useTheme();
  useDensity();

  // ⌘K command palette. State + global listener live at the app root
  // so any route can dispatch the `toleranz:open-cmdk` event (the
  // AppHeader SearchInput button does exactly that). The palette
  // itself is keyboard-trapped while open via the Esc listener in
  // CommandPalette.jsx.
  const [paletteOpen, setPaletteOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    const onCustom = () => setPaletteOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('toleranz:open-cmdk', onCustom);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('toleranz:open-cmdk', onCustom);
    };
  }, []);

  return (
    <Suspense fallback={<RouteFallback />}>
      <ScrollToTop />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <PlausibleAnalytics />
      {/* Global toast surface — call `toast.success/error/info(msg)`
          from anywhere to surface non-blocking feedback. Replaces the
          old blocking window.alert() calls in the project save flow. */}
      <Toasts />
      {/* Datenschutz-Hinweis — one-time notice that we don't use
          tracking cookies. Renders fixed bottom-right, dismissable;
          out of scope for the calc UI flow but reachable on every
          public route. */}
      <PrivacyNotice />
      <Routes>
        {/* Public auth routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/legal/terms" element={<TermsPage />} />
        <Route path="/legal/terms-consumer" element={<ConsumerTermsPage />} />
        <Route path="/legal/terms-business" element={<BusinessTermsPage />} />
        <Route path="/legal/privacy" element={<PrivacyPage />} />
        <Route path="/legal/subprocessors" element={<SubprocessorsPage />} />
        <Route path="/legal/avv" element={<AvvPage />} />
        <Route path="/legal/imprint" element={<ImprintPage />} />
        <Route path="/legal/sla" element={<SlaPage />} />
        <Route path="/legal/services" element={<ServiceDescriptionPage />} />
        <Route path="/faq" element={<FAQPage />} />

        {/* Protected calculator route */}
        <Route path="/" element={
          <AuthGuard>
            <CalculatorApp />
          </AuthGuard>
        } />

        {/* Projects Management */}
        <Route path="/projects" element={
          <AuthGuard>
            <ProjectsPage />
          </AuthGuard>
        } />

        {/* Settings */}
        <Route path="/settings" element={
          <AuthGuard>
            <SettingsLayout />
          </AuthGuard>
        }>
          <Route index element={<AccountSettings />} />
          <Route path="profile" element={<ProfileSettings />} />
          <Route path="display" element={<DisplaySettings />} />
          <Route path="billing" element={<BillingSettings />} />
          <Route path="montecarlo" element={<MonteCarloSettings />} />
        </Route>

        {/* Admin */}
        <Route path="/admin" element={
          <AuthGuard requireAdmin>
            <AdminLayout />
          </AuthGuard>
        }>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<UserList />} />
          <Route path="audit" element={<AuditLog />} />
        </Route>

        {/* Catch-all 404. Must remain LAST so every more-specific
            route above wins. */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default App;